import { datadogLogs } from '@datadog/browser-logs';
import type { ZodError } from 'zod';

/**
 * Observability for Bizy schema drift.
 *
 * Both the history-rehydration transform and the live `node_appended` handler
 * drop a node that fails the FE Zod contract rather than failing the whole
 * Document. That graceful degradation is intentional, but a silent drop hides
 * a real signal: the backend produced a node shape the FE can't render — a new
 * node `type`, or a payload whose shape drifted from the DocumentShape the FE
 * mirrors. Logging the drop surfaces that mismatch so it can be caught and the
 * FE schema brought back in sync, instead of content vanishing unnoticed.
 *
 * `source` distinguishes a live stream drop (current backend ↔ current FE
 * drift, the high-signal case) from history rehydration (which also tolerates
 * legacy/forward-compat persisted data).
 */
export function logDroppedBizyNode(source: 'stream' | 'rehydrate', rawNode: unknown, error: ZodError): void {
	const nodeType = rawNode && typeof rawNode === 'object' && 'type' in rawNode ? (rawNode as { type: unknown }).type : undefined;
	datadogLogs.logger.warn('[Bizy] dropped unparseable assistant-message node', {
		bizyNodeSource: source,
		bizyNodeType: typeof nodeType === 'string' ? nodeType : String(nodeType),
		bizyNodeIssues: error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`).join('; '),
	});
}
